"use client"

import { EmailBlockContent, Theme, defaultTheme } from "@/types"
import { useState, useEffect } from "react"
import { BlockImage } from "../BlockImage"
import { RichTextRenderer } from "../RichTextRenderer"

interface EmailBlockProps {
  content: EmailBlockContent
  onAnswer?: (answer: string) => void
  answer?: string
  theme?: Theme | null
  previewMode?: "desktop" | "mobile"
}

// Email validation regex
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/

export function EmailBlock({ content, onAnswer, answer: initialAnswer, theme = null }: EmailBlockProps) {
  const [email, setEmail] = useState("")
  const [isValid, setIsValid] = useState(true)
  const [hasBlurred, setHasBlurred] = useState(false)
  
  // Use provided theme or fallback to default
  const activeTheme = theme || defaultTheme

  // Initialize with existing answer if provided
  useEffect(() => {
    if (initialAnswer !== undefined) {
      setEmail(initialAnswer)
      setIsValid(EMAIL_REGEX.test(initialAnswer))
    }
  }, [initialAnswer])

  const validateEmail = (value: string): boolean => {
    return EMAIL_REGEX.test(value.trim())
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    const valid = value.trim() === "" || validateEmail(value)
    setIsValid(valid)
    
    // Only call onAnswer if email is valid
    if (valid && value.trim() !== "") {
      onAnswer?.(value.trim())
    } else {
      // Clear answer if invalid
      onAnswer?.("")
    }
  }

  const handleBlur = () => {
    setHasBlurred(true)
    if (email.trim() !== "") {
      const valid = validateEmail(email)
      setIsValid(valid)
    }
  }

  return (
    <div>
      <h3 
        className="text-2xl font-semibold mb-3 text-center"
        style={{ 
          color: activeTheme.colors.text,
          fontFamily: activeTheme.fonts.heading
        }}
      >
        <RichTextRenderer content={content.question} />
      </h3>

      <div className="max-w-2xl mx-auto">
        <input
          type="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={content.placeholder || "your@email.com"}
          className={`w-full px-6 py-4 text-lg border-2 rounded-xl shadow-sm focus:outline-none focus-visible:outline-none transition-all ${
            hasBlurred && !isValid ? 'border-red-500' : 'border-gray-200'
          }`}
          style={{
            boxShadow: 'none',
          } as React.CSSProperties}
          onFocus={(e) => {
            const focusColor = isValid ? activeTheme.colors.primary : '#ef4444'
            e.target.style.borderColor = focusColor
            e.target.style.boxShadow = `0 0 0 2px ${focusColor}40`
          }}
          onBlur={(e) => {
            e.target.style.borderColor = hasBlurred && !isValid ? '#ef4444' : '#e5e7eb'
            e.target.style.boxShadow = 'none'
          }}
        />
        {hasBlurred && !isValid && email.trim() !== "" && (
          <p className="mt-2 text-sm text-red-500 text-center">
            Please enter a valid email address
          </p>
        )}
      </div>
    </div>
  )
}

